'use client';

import { c as _c } from "react/compiler-runtime";
import { checkAccess, useAuth } from '@repo/auth';
import { permissions, roleNames } from '@repo/permissions';
import { TEST_IDS } from '@repo/sdk/constants';
import { AdminCmdK } from '@repo/shortcuts/admin';
import { type CommandWorkflow, type CommandWorkflowGroup, type CommandWorkflowLinkNode, type CommandWorkflowPage, defineCommandWorkflow } from '@repo/shortcuts/command';
import { Kbd, KbdGroup } from '@repo/ui/components';
import { ArrowRightLeft, Building, Caravan, Container, Factory, KeyRound, type LucideIcon, Plus, Search, Shield, Users } from '@repo/ui/icons';
import { cn } from '@repo/ui/lib/utils';
import { type ReactNode, useMemo } from 'react';
import { CommandLabel } from './command-label.tsx';
import { useCommandRegistry } from './command-provider.tsx';
import { GlobalSearchResultsRender } from './global-search-results-render.tsx';
import { useGodMode } from './god-mode-provider.tsx';
import { LoadListRender } from './load-list-render.tsx';
import { ScopedCmdKShortcuts } from './scoped-cmdk-shortcuts.tsx';
import { useRouteCatalog } from './use-route-catalog.ts';
import { getLoadCreationCapabilities, loadCreationAccessRequirements } from '@/app/@protected/loads/_utils/load-creation-access.ts';
type BuildWorkflowInput = {
  canUseGodMode: boolean;
  isGodModeEnabled: boolean;
  registrations: ReturnType<typeof useCommandRegistry>['registrations'];
  routeCommands: CommandWorkflowLinkNode[];
  toggleGodMode: () => void;
  user: ReturnType<typeof useAuth>['user'];
};
const GodModeShortcut = t0 => {
  const $ = _c(15);
  const {
    active
  } = t0;
  const t1 = active && "opacity-0";
  let t2;
  if ($[0] !== t1) {
    t2 = cn("col-start-1 row-start-1 transition-opacity duration-[120ms] ease-(--god-mode-ease-out) motion-reduce:duration-100", t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <Kbd data-sentry-element="Kbd" data-sentry-source-file="global-cmdk.tsx">G</Kbd>;
    t4 = <Kbd data-sentry-element="Kbd" data-sentry-source-file="global-cmdk.tsx">G</Kbd>;
    $[2] = t3;
    $[3] = t4;
  } else {
    t3 = $[2];
    t4 = $[3];
  }
  let t5;
  if ($[4] !== active || $[5] !== t2) {
    t5 = <KbdGroup aria-hidden={active} className={t2} data-sentry-element="KbdGroup" data-sentry-source-file="global-cmdk.tsx">{t3}{t4}</KbdGroup>;
    $[4] = active;
    $[5] = t2;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const t6 = !active;
  const t7 = active ? "opacity-100" : "opacity-0";
  let t8;
  if ($[7] !== t7) {
    t8 = cn("col-start-1 row-start-1 rounded-sm bg-primary/10 px-1.5 py-0.5 text-[10px] font-semibold tracking-wider text-primary uppercase ring-1 ring-primary/20 transition-opacity duration-[120ms] ease-(--god-mode-ease-out) motion-reduce:duration-100", t7);
    $[7] = t7;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  let t9;
  if ($[9] !== t6 || $[10] !== t8) {
    t9 = <span aria-hidden={t6} className={t8}>ACTIVE</span>;
    $[9] = t6;
    $[10] = t8;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] !== t5 || $[13] !== t9) {
    t10 = <span className="relative inline-grid min-w-14 justify-items-end" data-sentry-component="GodModeShortcut" data-sentry-source-file="global-cmdk.tsx">{t5}{t9}</span>;
    $[12] = t5;
    $[13] = t9;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  return t10;
};
const routeIcons: Record<string, LucideIcon> = {
  brokers: ArrowRightLeft,
  carriers: Caravan,
  customers: Factory,
  loads: Container,
  'other-companies': Building,
  permissions: KeyRound,
  users: Users
};
const buildWorkflow = ({
  canUseGodMode,
  isGodModeEnabled,
  registrations,
  routeCommands,
  toggleGodMode,
  user
}: BuildWorkflowInput): CommandWorkflow => {
  const rootGroups: CommandWorkflowGroup[] = [];
  for (const reg of registrations) {
    if (reg.groups && reg.groups.length > 0) {
      for (const group of reg.groups) {
        rootGroups.push(group);
      }
    }
  }
  const canListLoads = checkAccess(user, {
    match: 'any',
    permissions: [permissions.loads.list],
    roles: [roleNames.superAdmin]
  }).allowed;
  const canListCompaniesByType = checkAccess(user, {
    match: 'any',
    permissions: [permissions.companies.listByType],
    roles: [roleNames.superAdmin]
  }).allowed;
  const canListUsers = checkAccess(user, {
    match: 'any',
    permissions: [permissions.users.list],
    roles: [roleNames.superAdmin]
  }).allowed;
  const canSearch = canListLoads || canListCompaniesByType || canListUsers;
  if (canSearch) {
    rootGroups.push({
      id: 'active-search',
      items: [{
        id: 'active-search-render',
        kind: 'render',
        render: context => <GlobalSearchResultsRender canListCompaniesByType={canListCompaniesByType} canListLoads={canListLoads} canListUsers={canListUsers} context={context} />
      }]
    });
  }
  const loadCreationAccess = getLoadCreationCapabilities({
    canCreateDraft: checkAccess(user, loadCreationAccessRequirements.createDraft).allowed,
    canCreateNormalLoad: checkAccess(user, loadCreationAccessRequirements.createNormalLoad).allowed,
    canPublishDraft: checkAccess(user, loadCreationAccessRequirements.publishDraft).allowed
  });
  const loadItems: CommandWorkflowGroup['items'] = [];
  if (loadCreationAccess.canOpenNewPage) {
    loadItems.push({
      href: '/loads/new',
      id: 'create-new-load',
      keywords: ['new', 'create', 'add', 'load', 'order'],
      kind: 'link',
      label: <CommandLabel icon={Plus}>
          {loadCreationAccess.canCreateNormalLoad ? 'Create new load' : 'Create new draft'}
        </CommandLabel>,
      shortcut: 'N L',
      value: 'create-new-load'
    });
  }
  if (canListLoads) {
    loadItems.push({
      id: 'search-loads',
      keywords: ['loads', 'orders', 'shipments', 'search', 'find'],
      kind: 'page',
      label: <CommandLabel icon={Search}>Search loads</CommandLabel>,
      pageId: 'loads-list',
      value: 'search-loads'
    });
  }
  if (loadItems.length > 0) {
    rootGroups.push({
      heading: 'Loads',
      id: 'loads',
      items: loadItems
    });
  }
  if (canListCompaniesByType) {
    rootGroups.push({
      heading: 'Companies',
      id: 'companies',
      items: [{
        id: 'search-companies',
        keywords: ['companies', 'customers', 'carriers', 'hauliers', 'brokers', 'other', 'search', 'find'],
        kind: 'page',
        label: <CommandLabel icon={Search}>Search companies</CommandLabel>,
        pageId: 'companies-list',
        value: 'search-companies'
      }]
    });
  }
  if (routeCommands.length > 0) {
    rootGroups.push({
      heading: 'Navigation',
      id: 'navigation',
      items: routeCommands.map(rc => {
        const Icon = rc.value ? routeIcons[rc.value] : undefined;
        return {
          ...rc,
          label: Icon ? <CommandLabel icon={Icon}>{rc.label}</CommandLabel> : rc.label
        };
      })
    });
  }
  if (canUseGodMode) {
    rootGroups.push({
      heading: 'God mode',
      id: 'god-mode',
      items: [{
        closeOnSelect: false,
        id: 'toggle-god-mode',
        keywords: ['god', 'impersonate', 'impersonation', 'super-admin', 'debug'],
        kind: 'action',
        label: <CommandLabel icon={Shield} iconClassName={cn('transition-colors duration-[120ms] ease-(--god-mode-ease-out) motion-reduce:duration-100', isGodModeEnabled && 'fill-primary text-primary!')}>
              {isGodModeEnabled ? 'Disable god mode' : 'Enable god mode'}
            </CommandLabel>,
        onSelect: toggleGodMode,
        shortcut: <GodModeShortcut active={isGodModeEnabled} />,
        testId: TEST_IDS.cmdk.godModeToggle,
        value: 'toggle-god-mode'
      }]
    });
  }
  const nestedPages: Record<string, CommandWorkflowPage> = {
    'companies-list': {
      groups: [{
        id: 'companies-search-group',
        items: [{
          id: 'companies-search-render',
          kind: 'render',
          render: context => <GlobalSearchResultsRender canListCompaniesByType={canListCompaniesByType} context={context} scope="companies" />
        }]
      }],
      inputPlaceholder: 'Search companies...',
      title: 'Search Companies'
    },
    'loads-list': {
      groups: [{
        id: 'loads-search-group',
        items: [{
          id: 'loads-search-render',
          kind: 'render',
          render: context => <LoadListRender context={context} />
        }]
      }],
      inputPlaceholder: 'Search loads by ID or company...',
      title: 'Search Loads'
    }
  };
  for (const reg of registrations) {
    if (reg.nestedPages) {
      Object.assign(nestedPages, reg.nestedPages);
    }
  }
  return defineCommandWorkflow({
    options: {
      resetSearchOnPageChange: true
    },
    pages: {
      ...nestedPages,
      root: {
        groups: rootGroups
      }
    },
    rootPageId: 'root'
  });
};
export const GlobalCmdK = t0 => {
  const $ = _c(13);
  const {
    children
  } = t0;
  const {
    registrations
  } = useCommandRegistry();
  const routeCommands = useRouteCatalog();
  const {
    user
  } = useAuth();
  const {
    canUseGodMode,
    isGodModeEnabled,
    toggleGodMode
  } = useGodMode();
  let t1;
  if ($[0] !== canUseGodMode || $[1] !== isGodModeEnabled || $[2] !== registrations || $[3] !== routeCommands || $[4] !== toggleGodMode || $[5] !== user) {
    t1 = buildWorkflow({
      canUseGodMode,
      isGodModeEnabled,
      registrations,
      routeCommands,
      toggleGodMode,
      user
    });
    $[0] = canUseGodMode;
    $[1] = isGodModeEnabled;
    $[2] = registrations;
    $[3] = routeCommands;
    $[4] = toggleGodMode;
    $[5] = user;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const workflow = t1;
  let t2;
  if ($[7] !== workflow) {
    t2 = <ScopedCmdKShortcuts workflow={workflow} data-sentry-element="ScopedCmdKShortcuts" data-sentry-source-file="global-cmdk.tsx" />;
    $[7] = workflow;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== children || $[10] !== t2 || $[11] !== workflow) {
    t3 = <AdminCmdK workflow={workflow} data-sentry-element="AdminCmdK" data-sentry-component="GlobalCmdK" data-sentry-source-file="global-cmdk.tsx">{t2}{children}</AdminCmdK>;
    $[9] = children;
    $[10] = t2;
    $[11] = workflow;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  return t3;
};